import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { locationService, customerService } from '../api';
import { Button } from './Button';
import { Input } from './Input';
import { ThemedText } from './ThemedText';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { extractItems } from '../utils/responseUtils';
import { X } from 'lucide-react';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const PacContainerStyle = createGlobalStyle`
  .pac-container {
    z-index: 10001 !important;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.md};
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const MapContainer = styled.div`
  width: 100%;
  height: 300px;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border};
`;

const AutocompleteInput = styled.input`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const DEFAULT_CENTER = { lat: 43.65, lng: -79.38 };

const resolveData = extractItems;

const LocationFormModal = ({ isOpen, onClose, onSuccess, editLocation, preselectedCustomerId }) => {
  const theme = useTheme();
  const autocompleteRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    customerId: '',
    latitude: '',
    longitude: '',
  });

  const [markerPosition, setMarkerPosition] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (editLocation) {
      setFormData({
        name: editLocation.name || '',
        address: editLocation.address || '',
        city: editLocation.city || '',
        state: editLocation.state || '',
        zipCode: editLocation.zipCode || '',
        country: editLocation.country || '',
        customerId: editLocation.customerId ? String(editLocation.customerId) : '',
        latitude: editLocation.latitude != null ? String(editLocation.latitude) : '',
        longitude: editLocation.longitude != null ? String(editLocation.longitude) : '',
      });
      if (editLocation.latitude != null && editLocation.longitude != null) {
        setMarkerPosition({
          lat: parseFloat(editLocation.latitude),
          lng: parseFloat(editLocation.longitude),
        });
      } else {
        setMarkerPosition(null);
      }
    } else {
      resetForm();
      if (preselectedCustomerId) {
        setFormData(prev => ({ ...prev, customerId: String(preselectedCustomerId) }));
      }
    }
  }, [editLocation, isOpen, preselectedCustomerId]);

  const fetchCustomers = async () => {
    try {
      const response = await customerService.getCustomers();
      setCustomers(resolveData(response));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      customerId: '',
      latitude: '',
      longitude: '',
    });
    setMarkerPosition(null);
  };

  const onAutocompleteLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
    autocomplete.setFields(['geometry', 'address_components', 'formatted_address']);
  }, []);

  const onPlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place || !place.geometry || !place.geometry.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const updates = {
      latitude: String(lat),
      longitude: String(lng),
    };

    // Parse address components
    if (place.address_components) {
      let streetNumber = '';
      let route = '';

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (types.includes('locality') || types.includes('sublocality_level_1')) {
          updates.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          updates.state = component.short_name;
        }
        if (types.includes('postal_code')) {
          updates.zipCode = component.long_name;
        }
        if (types.includes('country')) {
          updates.country = component.long_name;
        }
      });

      updates.address = [streetNumber, route].filter(Boolean).join(' ');
    }

    setFormData(prev => ({ ...prev, ...updates }));
    setMarkerPosition({ lat, lng });
  }, []);

  const handleMarkerDragEnd = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setFormData(prev => ({ ...prev, latitude: String(lat), longitude: String(lng) }));
    setMarkerPosition({ lat, lng });

    // Reverse geocode
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const updates = {};
          let streetNumber = '';
          let route = '';

          results[0].address_components.forEach((component) => {
            const types = component.types;
            if (types.includes('street_number')) streetNumber = component.long_name;
            if (types.includes('route')) route = component.long_name;
            if (types.includes('locality') || types.includes('sublocality_level_1')) updates.city = component.long_name;
            if (types.includes('administrative_area_level_1')) updates.state = component.short_name;
            if (types.includes('postal_code')) updates.zipCode = component.long_name;
            if (types.includes('country')) updates.country = component.long_name;
          });

          updates.address = [streetNumber, route].filter(Boolean).join(' ');
          setFormData(prev => ({ ...prev, ...updates }));
        }
      });
    }
  }, []);

  const handleLatLngChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    const lat = field === 'latitude' ? parseFloat(value) : parseFloat(formData.latitude);
    const lng = field === 'longitude' ? parseFloat(value) : parseFloat(formData.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setMarkerPosition({ lat, lng });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      customerId: formData.customerId ? parseInt(formData.customerId) : null,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
    };

    if (!payload.name || !payload.address || !payload.city || !payload.state || !payload.zipCode || !payload.country) {
      toast.error('Name, Address, City, State, Zip Code, and Country are required');
      return;
    }

    try {
      if (editLocation) {
        const updated = await locationService.updateLocation(editLocation.id, payload);
        toast.success('Location updated successfully');
        onSuccess && onSuccess(updated);
      } else {
        const created = await locationService.createLocation(payload);
        toast.success('Location created successfully');
        onSuccess && onSuccess(created);
      }
      resetForm();
      onClose();
    } catch (error) {
      console.error('Failed to save location:', error);
      toast.error(error.response?.data?.message || `Failed to ${editLocation ? 'update' : 'create'} location`);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const mapCenter = markerPosition || DEFAULT_CENTER;

  return (
    <Modal theme={theme} isOpen={isOpen}>
      <PacContainerStyle />
      <ModalContent theme={theme}>
        <ModalHeader theme={theme}>
          <ThemedText type="subtitle">{editLocation ? 'Edit Location' : 'New Location'}</ThemedText>
          <Button variant="secondary" size="sm" onClick={handleClose}>
            <X size={16} />
          </Button>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody theme={theme}>
            <FormGroup theme={theme}>
              <Label theme={theme}>Name *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter location name"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Address Search</Label>
              {isLoaded ? (
                <Autocomplete
                  onLoad={onAutocompleteLoad}
                  onPlaceChanged={onPlaceChanged}
                >
                  <AutocompleteInput
                    theme={theme}
                    placeholder="Search for an address..."
                    type="text"
                  />
                </Autocomplete>
              ) : (
                <AutocompleteInput
                  theme={theme}
                  placeholder="Loading Google Maps..."
                  disabled
                />
              )}
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Address *</Label>
              <Input
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter address"
              />
            </FormGroup>

            <FormRow theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>City *</Label>
                <Input
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Enter city"
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>State *</Label>
                <Input
                  required
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="Enter state"
                />
              </FormGroup>
            </FormRow>

            <FormRow theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Zip Code *</Label>
                <Input
                  required
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  placeholder="Enter zip code"
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Country *</Label>
                <Input
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Enter country"
                />
              </FormGroup>
            </FormRow>

            <FormGroup theme={theme}>
              <Label theme={theme}>Customer</Label>
              <Select
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                theme={theme}
              >
                <option value="">Select customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>{customer.firstName} {customer.lastName}{customer.company ? ` (${customer.company})` : ''}</option>
                ))}
              </Select>
            </FormGroup>

            <FormRow theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => handleLatLngChange('latitude', e.target.value)}
                  placeholder="Enter latitude"
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => handleLatLngChange('longitude', e.target.value)}
                  placeholder="Enter longitude"
                />
              </FormGroup>
            </FormRow>

            {isLoaded && (
              <FormGroup theme={theme}>
                <Label theme={theme}>Map</Label>
                <MapContainer theme={theme}>
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={markerPosition ? 15 : 4}
                    onClick={(e) => {
                      const lat = e.latLng.lat();
                      const lng = e.latLng.lng();
                      setFormData(prev => ({ ...prev, latitude: String(lat), longitude: String(lng) }));
                      setMarkerPosition({ lat, lng });
                    }}
                  >
                    {markerPosition && (
                      <Marker
                        position={markerPosition}
                        draggable
                        onDragEnd={handleMarkerDragEnd}
                      />
                    )}
                  </GoogleMap>
                </MapContainer>
              </FormGroup>
            )}
          </ModalBody>

          <ModalFooter theme={theme}>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editLocation ? 'Update Location' : 'Create Location'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default LocationFormModal;
export { LocationFormModal };
